<template>
  <div class="panel-card">
    <el-button type="primary" :icon="Plus" @click="openDialog">{{ $str.getKey('设备升级/新增升级包') }}</el-button>
  </div>

  <div class="panel-card">
    <el-table :data="list" v-loading="loading">
      <el-table-column :label="$str.getKey('设备升级/文件名')" prop="fileName" show-overflow-tooltip></el-table-column>
      <el-table-column :label="$str.getKey('设备升级/文件类型')" prop="fileType" width="120">
        <template #default="{ row }">
          <el-tag :type="getFileTagType(row.fileType)">{{ row.fileType }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column :label="$str.getKey('设备升级/文件大小')" prop="fileSize" width="100">
        <template #default="{ row }">
          {{ formatBytes(row.fileSize * 1) }}
        </template>
      </el-table-column>
      <el-table-column label="md5" prop="fileMd5" show-overflow-tooltip></el-table-column>
      <el-table-column :label="$str.getKey('设备升级/标签')" prop="stationTagName"></el-table-column>
      <el-table-column :label="$str.getKey('设备升级/上传时间')" prop="createTime" width="180"></el-table-column>
      <el-table-column :label="$str.getKey('设备升级/操作')" width="200">
        <template #default="{ row }">
          <el-button type="primary" @click="onDownload(row)" text :disabled="row.fileType === 'ELINK_KEY'">{{ $str.getKey('设备升级/下载') }}</el-button>
          <el-button type="danger" @click="onRemove(row)" text>{{ $str.getKey('设备升级/删除') }}</el-button>
        </template>
      </el-table-column>
    </el-table>

    <Pagination v-model="pageNum" :total="total" :page-size="pageSize" @change="fetchList" />
  </div>

  <el-dialog v-model="isOpen" :title="$str.getKey('设备升级/上传升级包')" class="ota-dialog">
    <el-form ref="uploadFormRef" :model="uploadForm" :rules="rules" :disabled="confirmLoading" label-width="150px" label-position="top">
      <el-form-item :label="$str.getKey('设备升级/选择标签')" required prop="selectedTag">
        <el-select v-model="selectedTag" style="width: 100%;" filterable>
          <el-option :label="tag.tagName" :value="tag.id" v-for="tag in tagOptions" :key="tag.id"></el-option>
        </el-select>
      </el-form-item>

      <el-form-item :label="$str.getKey('设备升级/请选择文件类型')" prop="fileType">
        <el-radio-group v-model="fileType">
          <el-radio label="BCS"></el-radio>
          <el-radio label="PCS"></el-radio>
          <el-radio label="BMU"></el-radio>
          <el-radio label="ELINK"></el-radio>
          <el-radio label="ELINK_KEY"></el-radio>
          <el-radio label="EMS"></el-radio>
          <el-radio label="WATER">Chiller</el-radio>
          <el-radio label="FIRE">Fire-Fighting</el-radio>
        </el-radio-group>
      </el-form-item>

      <el-form-item :label="$str.getKey('设备升级/文件上传')" prop="fileList">
        <el-upload class="du-upload" drag action="/cloud/device/upgrade/file/upload" name="deviceFile"
          :before-upload="onBeforeUpload" :headers="headers" :on-success="onSuccess" :limit="1"
          v-model:file-list="fileList" :disabled="confirmLoading">
          <el-icon class="el-icon--upload"><upload-filled /></el-icon>
          <div class="el-upload__text">{{ $str.getKey('设备升级/拖拽到此或') }} <em>{{ $str.getKey('设备升级/点击上传') }}</em></div>
          <template #tip>
            <div class="el-upload__tip">{{ $str.getKey('设备升级/请选择文件') }}, {{ $str.getKey('设备升级/最大不超过') }} 50Mb</div>
          </template>
        </el-upload>
      </el-form-item>
    </el-form>

    <template #footer>
      <el-button @click="onCalcel" :disabled="confirmLoading">{{ $str.getKey('设备升级/取消') }}</el-button>
      <el-button type="primary" @click="onConfirm(uploadFormRef)" :loading="confirmLoading">{{ $str.getKey('设备升级/确定') }}</el-button>
    </template>
  </el-dialog>
</template>

<script setup>
import { computed, reactive, ref } from 'vue'
import { Plus, UploadFilled } from '@element-plus/icons-vue'
import { addPackage, getFileList, getTagSelectOptions, removePackage } from '@/api/upgrade'
import { useList, getFileTagType } from './feature'
import { getToken } from "@/utils/auth"
import { formatBytes } from '@/utils'
import { eui } from 'ems-ui'
import { ElMessageBox } from 'element-plus'
import BMF from 'browser-md5-file'
import Pagination from '@/components/Pagination/index.vue'

const $str = window.$str
const isOpen = ref(false)
const selectedTag = ref('')
const tagOptions = ref([])
const uploadFormRef = ref(null)
const fileList = ref([])
const fileType = ref('')

const uploadForm = reactive({
  selectedTag,
  fileList,
  fileType,
})

const rules = reactive({
  selectedTag: [
    { required: true, message: $str.getKey('设备升级/请先选择标签'), trigger: 'blur' },
  ],
  fileList: [
    {
      type: 'array',
      required: true,
      message: $str.getKey('设备升级/请先上传文件'),
      trigger: 'change',
    },
  ],
  fileType: [
    { required: true, message: $str.getKey('设备升级/请先选择文件类型'), trigger: 'blur' },
  ]
})

getTagSelectOptions()
  .then(res => {
    tagOptions.value = res.data
  })
  .catch((e) => {
    console.warn(e)
  })

function openDialog() {
  isOpen.value = true
  fileList.value.length = 0
  selectedTag.value = ''
  fileType.value = ''
}

function onCalcel() {
  isOpen.value = false
}

const confirmLoading = ref(false)

function onConfirm(comp) {
  comp.validate(async success => {
    if (success) {
      const file = fileList.value[0]
      const fileMd5 = await getFileMd5(file.raw)
      try {
        confirmLoading.value = true
        await addPackage({
          fileName: file.name,
          fileUrl: file.response.data,
          fileMd5: fileMd5,
          fileSize: file.size,
          stationTag: selectedTag.value,
          fileType: fileType.value
        })

        eui.showTopTip($str.getKey('设备升级/上传成功'), 'success')
        fetchList()
        isOpen.value = false
      } catch (error) {
        console.warn(error)
      } finally {
        confirmLoading.value = false
      }
    }
  })
}

function getFileMd5(file) {
  return new Promise((resolve, reject) => {
    const bmf = new BMF()
    bmf.md5(file, (err, md5) => {
      if (err) {
        reject(err)
      } else {
        resolve(md5)
      }
    },
      progress => {
        console.log('progress number:', progress)
      },
    )
  })
}

function onBeforeUpload(rawFile) {
  const fileSizeOk = rawFile.size <= 50 * 1024 * 1024

  if (!fileSizeOk) {
    eui.showTopTip($str.getKey('设备升级/文件太大，请选择小于50Mb的文件'))
    return false
  }
}

function onSuccess(response) {
  console.log('upload response', response)
}

const headers = computed(() => {
  return {
    Authorization: 'Bearer ' + getToken()
  }
})

function onDownload(row) {
  let aTag = document.createElement('a')
  aTag.href = row.fileUrl
  aTag.download = row.fileName
  aTag.target = '_blank'
  document.body.appendChild(aTag)
  aTag.click()
  document.body.removeChild(aTag)
}

function onRemove(row) {
  ElMessageBox.confirm($str.getKey('设备升级/确认要删除吗') + '？', $str.getKey('设备升级/提示'), {
    confirmButtonText: $str.getKey('设备升级/确定'),
    cancelButtonText: $str.getKey('设备升级/取消'),
    type: 'warning'
  })
    .then(() => removePackage(row.id))
    .then(() => {
      eui.showTopTip($str.getKey('设备升级/成功'), 'success')
      fetchList()
    })
    .catch(() => { })
}

const { list, pageNum, pageSize, total, loading, fetchList } = useList(({ pageNum, pageSize }) => {
  return getFileList(null, null, pageNum, pageSize)
})
</script>

<style lang='scss' src="./style.scss"></style>
